import { XCircleIcon } from '@heroicons/react/24/solid'
import { cn } from '@heroui/react'
import { useBulkPermanentDelete, useBulkRestore } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore, useIsOverQuota } from '@qse/stores'
import React, { useEffect, useRef, useState } from 'react'

import {
  CopyIconDrop,
  DeleteIcon,
  DownloadIconDrop,
  MoveIcon,
  PreviewIcon,
  RenameIcon,
  StarIconMenu,
} from '../icons'

export type ContextMenuAction =
  | 'download'
  | 'preview'
  | 'rename'
  | 'duplicate'
  | 'favourite'
  | 'trash'
  | 'move'
  | 'restore'
  | 'deletePermanently'
  | 'abortUpload'

interface ContextMenuProps {
  isOpen: boolean
  position: { x: number; y: number }
  path?: string
  fileId?: string
  selectedFileIds?: (string | number)[]
  onClose: () => void
  onRequestClose?: () => void
  onAction: (action: ContextMenuAction, path?: string) => void
  showRestore?: boolean
  trashMode?: boolean
  fileStatus?: string
  isFolder?: boolean
  fileData?: any
  isMarqueeSelecting?: boolean
  onDeleteGhostFiles?: (fileIds: string[]) => Promise<void>
  systemFolderType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  position,
  onAction,
  path,
  fileId,
  selectedFileIds,
  showRestore = false,
  trashMode = false,
  fileStatus,
  isFolder = false,
  onRequestClose,
  fileData,
  isMarqueeSelecting = false,
  onDeleteGhostFiles,
  systemFolderType,
}) => {
  const isOverQuota = useIsOverQuota()
  const { mutateAsync: deleteForever } = useBulkPermanentDelete()
  const { mutateAsync: restoreFiles } = useBulkRestore()
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const menuItemClickedRef = useRef(false)
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })
  const [isPositioned, setIsPositioned] = useState(false)

  // Calculate optimal menu position
  useEffect(() => {
    if (isOpen) {
      // Set initial position immediately to prevent flash
      let x = position.x + 5
      let y = position.y + 5

      // Basic overflow protection for initial position
      x = Math.max(10, Math.min(x, window.innerWidth - 260)) // 250px width + 10px margin
      y = Math.max(10, Math.min(y, window.innerHeight - 200)) // Approximate height + 10px margin

      setMenuPosition({ x, y })
      setIsPositioned(true)

      // Then recalculate with actual dimensions
      requestAnimationFrame(() => {
        if (contextMenuRef.current) {
          // Get actual menu dimensions after render
          const menuRect = contextMenuRef.current.getBoundingClientRect()
          const menuWidth = menuRect.width
          const menuHeight = menuRect.height

          // Start with cursor position
          x = position.x
          y = position.y

          // Add small offset to prevent menu from appearing directly under cursor
          x += 5
          y += 5

          // Adjust horizontal position if menu would overflow right edge
          if (x + menuWidth > window.innerWidth) {
            x = window.innerWidth - menuWidth - 10
          }

          // Adjust vertical position if menu would overflow bottom edge
          if (y + menuHeight > window.innerHeight) {
            y = window.innerHeight - menuHeight - 10
          }

          // Ensure menu doesn't go off-screen to the left or top
          x = Math.max(10, x)
          y = Math.max(10, y)

          setMenuPosition({ x, y })
        }
      })
    } else {
      setIsPositioned(false)
    }
  }, [isOpen, position, trashMode, fileData?.isFavorite])

  // Recalculate position after menu items are rendered
  useEffect(() => {
    if (isOpen && contextMenuRef.current) {
      // Use a small delay to ensure menu items are fully rendered
      const timeoutId = setTimeout(() => {
        const menuRect = contextMenuRef.current?.getBoundingClientRect()

        if (menuRect) {
          const menuWidth = menuRect.width
          const menuHeight = menuRect.height

          let x = menuPosition.x
          let y = menuPosition.y

          // Adjust horizontal position if menu would overflow right edge
          if (x + menuWidth > window.innerWidth) {
            x = window.innerWidth - menuWidth - 10
          }

          // Adjust vertical position if menu would overflow bottom edge
          if (y + menuHeight > window.innerHeight) {
            y = window.innerHeight - menuHeight - 10
          }

          // Ensure menu doesn't go off-screen to the left or top
          x = Math.max(10, x)
          y = Math.max(10, y)

          setMenuPosition({ x, y })
        }
      }, 50) // Small delay to ensure rendering is complete

      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, menuPosition.x, menuPosition.y])

  // Add window resize handler to reposition menu if needed
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && contextMenuRef.current) {
        const menuRect = contextMenuRef.current.getBoundingClientRect()
        const menuWidth = menuRect.width
        const menuHeight = menuRect.height

        let x = menuPosition.x
        let y = menuPosition.y

        // Ensure menu is still visible after resize
        if (x + menuWidth > window.innerWidth) {
          x = window.innerWidth - menuWidth - 10
        }
        if (y + menuHeight > window.innerHeight) {
          y = window.innerHeight - menuHeight - 10
        }

        x = Math.max(10, x)
        y = Math.max(10, y)

        setMenuPosition({ x, y })
      }
    }

    const handleScroll = () => {
      if (isOpen && typeof onRequestClose === 'function') {
        // Close menu on scroll to prevent positioning issues
        onRequestClose()
      }
    }

    if (isOpen) {
      window.addEventListener('resize', handleResize)
      window.addEventListener('scroll', handleScroll, true)

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [isOpen, menuPosition.x, menuPosition.y, onRequestClose])

  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault()
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    } else {
      document.body.style.overflow = 'unset'
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)
    }

    return () => {
      document.body.style.overflow = 'unset'
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)
    }
  }, [isOpen])

  // Add click outside handler to auto-close context menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return

      const target = event.target as Node

      // Check if click is inside the context menu
      if (contextMenuRef.current && contextMenuRef.current.contains(target)) {
        return
      }

      // Check if click is on a menu item specifically
      const isOnMenuItem =
        (target as Element).closest('[role="menuitem"]') ||
        (target as Element).closest('.context-menu-item') ||
        (target as Element).closest('.px-3') // Menu item styling class

      if (isOnMenuItem) {
        return
      }

      // Check if click is on a file card or within the file grid area
      const isOnFileCard =
        (target as Element).closest('.file-card') || (target as Element).closest('[data-file-id]')
      const isInFileGrid = (target as Element).closest('.grid') // Check if click is in the grid container

      if (isOnFileCard || isInFileGrid) {
        if (typeof onRequestClose === 'function') {
          onRequestClose()
        }

        return
      }

      // Don't close if a menu item was just clicked
      if (menuItemClickedRef.current) {
        menuItemClickedRef.current = false

        return
      }

      // Close the menu for any click outside
      if (typeof onRequestClose === 'function') {
        onRequestClose()
      }
    }

    if (isOpen) {
      // Use a small delay to prevent immediate closing when opening the menu
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true) // Use capture phase
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [isOpen, onRequestClose])

  // Close context menu when marquee selection starts
  useEffect(() => {
    if (isMarqueeSelecting && isOpen && typeof onRequestClose === 'function') {
      onRequestClose()
    }
  }, [isMarqueeSelecting, isOpen, onRequestClose])

  if (!isOpen || !isPositioned) return null

  // Don't show context menu for CHAT_ROOT folders (except in trash mode)
  if (systemFolderType === 'CHAT_ROOT' && !trashMode) {
    return null
  }

  // If in trash mode, show only restore and permanently delete
  if (trashMode) {
    const trashMenuItems = [
      {
        label: 'Restore',
        icon: () => (
          <svg
            className="w-4 h-4 text-gray-900 dark:text-white flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        ),
        action: 'restore',
        confirm: true,
      },
      {
        label: 'Delete Forever',
        icon: DeleteIcon,
        action: 'trash',
        confirm: true,
        shortcut: 'Delete',
      },
    ]

    const handleTrashMenuClick = ({ action, confirm }: { action: string; confirm?: boolean }) => {
      menuItemClickedRef.current = true
      if (typeof onRequestClose === 'function') onRequestClose() // Always close menu first
      let fileIds: string[] = []

      if (selectedFileIds && selectedFileIds.length > 0) {
        // If right-clicked file is in selection, use all selected
        if (fileId && selectedFileIds.includes(fileId)) {
          fileIds = selectedFileIds.map(id => id.toString())
        } else if (fileId) {
          fileIds = [fileId]
        } else {
          fileIds = selectedFileIds.map(id => id.toString())
        }
      } else if (fileId) {
        fileIds = [fileId]
      }
      if (confirm) {
        if (action === 'restore') {
          // Create dynamic title and message based on number of items
          const itemCount = fileIds.length
          let title: string

          if (itemCount === 1) {
            // Get the filename for single file restoration
            const fileName = fileData?.name || 'this item'

            title = `Restore "${fileName}"?`
          } else {
            title = `Restore ${itemCount} items?`
          }
          const message =
            itemCount === 1
              ? 'This file or folder will be restored from trash.'
              : 'These files and folders will be restored from trash.'

          useActiveModelStore.setState({
            width: 'max-w-[500px]',
            modelActive: ModelControlEnum.CONFIRMATION,
            data: {
              title,
              message,
              icon: '/assets/svg/warning.svg',
              actions: [
                {
                  label: 'Cancel',
                  variant: 'flat',
                },
                {
                  label: 'Restore',
                  color: 'primary',
                  variant: 'solid',
                  onPress: async () => {
                    await restoreFiles({ fileIds })
                  },
                },
              ],
            },
            toggleModel: true,
          })
        } else {
          // Create dynamic title and message based on number of items
          const itemCount = fileIds.length
          let title: string

          if (itemCount === 1) {
            // Get the filename for single file deletion
            const fileName = fileData?.name || 'this item'

            title = `Delete "${fileName}" Forever?`
          } else {
            title = `Delete ${itemCount} items Forever?`
          }
          const message =
            itemCount === 1
              ? 'This file or folder will be permanently deleted and cannot be restored.'
              : 'These files and folders will be permanently deleted and cannot be restored.'

          useActiveModelStore.setState({
            width: 'max-w-[490px]',
            modelActive: ModelControlEnum.CONFIRMATION,
            data: {
              title,
              message,
              icon: '/assets/svg/warning.svg',
              actions: [
                {
                  label: 'Cancel',
                  color: 'default',
                },
                {
                  label: 'Delete Forever',
                  color: 'danger',
                  variant: 'solid',
                  onPress: async () => {
                    await deleteForever({ fileIds })
                  },
                },
              ],
            },
            toggleModel: true,
          })
        }
      } else {
        onAction(action as ContextMenuAction, path || '')
      }
    }

    return (
      <div
        ref={contextMenuRef}
        className="context-menu fixed bg-white dark:bg-[#343537] w-[250px] border border-gray-300 dark:border-[#707070] rounded-xl shadow-xl z-[9999] py-2"
        style={{
          left: menuPosition.x,
          top: menuPosition.y,
        }}
        onClick={e => e.stopPropagation()}
      >
        {trashMenuItems.map(({ label, icon: Icon, action, confirm, shortcut }) => (
          <div
            key={action}
            className="px-3 sm:px-4 py-2 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#282828]/50 cursor-pointer flex items-center justify-between text-sm"
            role="menuitem"
            onClick={() => handleTrashMenuClick({ action, confirm })}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-4 h-4 text-gray-900 dark:text-white flex-shrink-0" />
              <span>{label}</span>
            </div>
            {shortcut && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{shortcut}</span>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Check if preview should be shown
  // If the item that triggered the action is NOT selected: allow preview
  // If the item IS selected: only allow preview if it's the ONLY selected item
  // Preview is now available for all file types (supported and unsupported)
  const isPreviewable =
    !isFolder &&
    fileData &&
    fileId != null &&
    // Case 1: The triggered item is not in the current selection
    (!selectedFileIds?.includes(fileId) ||
      // Case 2: The triggered item is selected but it's the only one selected
      (selectedFileIds?.length === 1 && selectedFileIds.includes(fileId)))

  // Check if this is a ghost file (exclude directories)
  const isGhostFile =
    !isFolder &&
    (fileStatus === 'UPLOADING' || fileStatus === 'ENCRYPTING' || fileStatus === 'FAILED')

  // Menu items - different logic for ghost files vs regular files vs system folders
  const menuItems = isGhostFile
    ? [
        // For ghost files, show ONLY the delete permanently action
        {
          label: 'Delete permanently',
          icon: XCircleIcon,
          action: 'deletePermanently',
          confirm: true,
          disabled: false,
          shortcut: undefined,
        },
      ]
    : [
        // Regular menu items for non-ghost files
        ...(isPreviewable
          ? [
              {
                label: 'Preview',
                icon: PreviewIcon,
                action: 'preview',
                disabled: systemFolderType === 'CHAT_CONVERSATION' && !trashMode,
                tooltip:
                  systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                    ? 'This action is disabled for conversation folders.'
                    : undefined,
              },
            ]
          : []),
        ...(!isFolder && selectedFileIds && selectedFileIds.length <= 20
          ? [
              {
                label: 'Download',
                icon: DownloadIconDrop,
                action: 'download',
                disabled:
                  (fileStatus && fileStatus !== 'AVAILABLE') ||
                  (systemFolderType === 'CHAT_CONVERSATION' && !trashMode),
                tooltip:
                  systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                    ? 'This action is disabled for conversation folders.'
                    : undefined,
              },
            ]
          : []),
        {
          label: 'Rename',
          icon: RenameIcon,
          action: 'rename',
          shortcut: 'F2',
          disabled: systemFolderType === 'CHAT_CONVERSATION' && !trashMode,
          tooltip:
            systemFolderType === 'CHAT_CONVERSATION' && !trashMode
              ? 'This action is disabled for conversation folders.'
              : undefined,
        },
        // Hide 'Make a copy' if isFolder is true
        ...(!isFolder
          ? [
              {
                label: 'Make a copy',
                icon: CopyIconDrop,
                action: 'duplicate',
                shortcut: 'Ctrl+C',
                disabled: (systemFolderType === 'CHAT_CONVERSATION' && !trashMode) || isOverQuota,
                tooltip: isOverQuota
                  ? 'Storage quota exceeded. Delete files to free up space.'
                  : systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                    ? 'This action is disabled for conversation folders.'
                    : undefined,
              },
            ]
          : []),
        {
          label: 'Move to',
          icon: MoveIcon,
          action: 'move',
          disabled: systemFolderType === 'CHAT_CONVERSATION' && !trashMode,
          tooltip:
            systemFolderType === 'CHAT_CONVERSATION' && !trashMode
              ? 'This action is disabled for conversation folders.'
              : undefined,
        },
        // Only show Favorites action when single file is selected (not in bulk mode)
        ...(!selectedFileIds || selectedFileIds.length <= 1
          ? [
              {
                label: fileData?.isFavorite === true ? 'Remove from favorites' : 'Favourites',
                icon: () => (
                  <StarIconMenu
                    className={`w-4 h-4 flex-shrink-0 ${
                      fileData?.isFavorite === true
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  />
                ),
                action: 'favourite',
                // Favourite action is always enabled
                disabled: false,
              },
            ]
          : []),
        ...(showRestore
          ? [
              {
                label: 'Restore',
                icon: () => (
                  <svg
                    className="w-4 h-4 text-gray-900 dark:text-white flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                ),
                action: 'restore',
              },
            ]
          : []),
        {
          label: 'Move to Trash',
          icon: DeleteIcon,
          action: 'trash',
          shortcut: 'Delete',
          disabled: systemFolderType === 'CHAT_CONVERSATION' && !trashMode,
          tooltip:
            systemFolderType === 'CHAT_CONVERSATION' && !trashMode
              ? 'This action is disabled for conversation folders.'
              : undefined,
        },
      ]

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={contextMenuRef}
      className={cn(
        // fileData?.isFavorite === true ? 'w-[220px] ' : 'w-[240px] ',
        'context-menu fixed bg-white dark:bg-[#343537] w-[250px] border border-gray-300 dark:border-[#707070] rounded-xl shadow-xl z-[9999] py-2',
      )}
      style={{
        left: menuPosition.x,
        top: menuPosition.y,
      }}
      onClick={e => e.stopPropagation()}
    >
      {menuItems.map(item => {
        const { label, icon: Icon, action, disabled, shortcut, tooltip } = item as any

        return (
          <div
            key={action}
            role="menuitem"
            title={tooltip}
            className={`px-3 sm:px-4 py-2 flex items-center justify-between text-sm ${
              disabled
                ? 'text-gray-500 dark:text-gray-500 cursor-not-allowed'
                : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#282828]/50 cursor-pointer'
            }`}
            onClick={() => {
              if (!disabled) {
                menuItemClickedRef.current = true
                if (typeof onRequestClose === 'function') onRequestClose()

                // Handle deletePermanently action with confirmation
                if (action === 'deletePermanently') {
                  const fileIds =
                    selectedFileIds && selectedFileIds.length > 0
                      ? selectedFileIds.map(id => id.toString())
                      : fileId
                        ? [fileId.toString()]
                        : []

                  if (fileIds.length > 0) {
                    const itemCount = fileIds.length
                    const title =
                      itemCount === 1
                        ? `Delete "${fileData?.name || 'this file'}" Permanently?`
                        : `Delete ${itemCount} files Permanently?`
                    const message =
                      itemCount === 1
                        ? 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove this file.'
                        : 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove these files.'

                    useActiveModelStore.setState({
                      width: 'max-w-[490px]',
                      modelActive: ModelControlEnum.CONFIRMATION,
                      data: {
                        title,
                        message,
                        icon: '/assets/svg/warning.svg',
                        actions: [
                          {
                            label: 'Cancel',
                            color: 'default',
                            variant: 'flat',
                          },
                          {
                            label: 'Delete Permanently',
                            color: 'danger',
                            variant: 'solid',
                            onPress: async () => {
                              // If onDeleteGhostFiles is available and this is a ghost file, use it
                              if (onDeleteGhostFiles && isGhostFile) {
                                await onDeleteGhostFiles(fileIds)
                              } else {
                                // Otherwise, call the regular onAction
                                onAction('deletePermanently' as ContextMenuAction, path || '')
                              }
                            },
                          },
                        ],
                      },
                      toggleModel: true,
                    })

                    return
                  }
                }

                onAction(action as ContextMenuAction, path || '')
              }
            }}
          >
            <div className="flex items-center gap-3">
              <Icon
                className={`w-4 h-4 flex-shrink-0 ${disabled ? 'text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}
              />
              <span>{label}</span>
            </div>
            {shortcut && (
              <span
                className={`text-xs ${disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}
              >
                {shortcut}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
